import type { UserRole } from '@/graphql/generated';

/**
 * Mapa de navegación. Cada destino declara qué roles lo ven, y de ahí salen
 * tanto el menú lateral como la paleta de comandos: una sola lista, para que no
 * se desincronicen.
 */
export interface NavItem {
  to: string;
  label: string;
  /** Nombre del icono de lucide (se resuelve en el componente). */
  icon: string;
  roles?: readonly UserRole[];
  /** Palabras extra por las que la paleta de comandos debe encontrarlo. */
  keywords?: readonly string[];
  /** Marca el destino como activo también en sus rutas hijas. */
  end?: boolean;
}

export interface NavSection {
  title: string;
  items: readonly NavItem[];
}

const ALL: readonly UserRole[] = ['ADMIN', 'SUPERVISOR', 'ASESOR', 'TECNICO', 'LECTURA'];
const MANAGEMENT: readonly UserRole[] = ['ADMIN', 'SUPERVISOR'];
const OPERATIONS: readonly UserRole[] = ['ADMIN', 'SUPERVISOR', 'ASESOR', 'TECNICO', 'LECTURA'];

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'Operación',
    items: [
      {
        to: '/',
        label: 'Tablero',
        icon: 'LayoutDashboard',
        roles: ALL,
        end: true,
        keywords: ['dashboard', 'indicadores', 'kpi', 'inicio'],
      },
      {
        to: '/tickets',
        label: 'Tickets',
        icon: 'Ticket',
        roles: OPERATIONS,
        keywords: ['reclamos', 'incidencias', 'casos', 'kanban'],
      },
      {
        to: '/bandeja',
        label: 'Bandeja',
        icon: 'MessagesSquare',
        roles: OPERATIONS,
        keywords: ['whatsapp', 'facebook', 'mensajes', 'chat', 'conversaciones'],
      },
      {
        to: '/clientes',
        label: 'Clientes',
        icon: 'Users',
        roles: OPERATIONS,
        keywords: ['agricultores', 'cartera', 'ficha', 'churn'],
      },
      {
        to: '/seguimientos',
        label: 'Seguimientos',
        icon: 'CalendarClock',
        roles: OPERATIONS,
        keywords: ['agenda', 'visitas', 'llamadas', 'mantenimiento', 'encuestas'],
      },
    ],
  },
  {
    title: 'Postventa',
    items: [
      {
        to: '/garantias',
        label: 'Garantías y repuestos',
        icon: 'ShieldCheck',
        roles: OPERATIONS,
        keywords: ['garantia', 'repuestos', 'partes', 'cobertura'],
      },
      {
        to: '/productos',
        label: 'Productos',
        icon: 'Package',
        roles: OPERATIONS,
        keywords: ['catalogo', 'stock', 'maquinaria', 'insumos', 'rentabilidad'],
      },
      {
        to: '/campanias',
        label: 'Campañas',
        icon: 'Megaphone',
        roles: MANAGEMENT,
        keywords: ['promociones', 'difusion', 'marketing', 'roi'],
      },
    ],
  },
  {
    title: 'Análisis',
    items: [
      {
        to: '/reportes',
        label: 'Reportes',
        icon: 'ChartNoAxesCombined',
        roles: ALL,
        keywords: ['fcr', 'mttr', 'retencion', 'recompra', 'churn', 'informes'],
      },
    ],
  },
  {
    title: 'Administración',
    items: [
      {
        to: '/configuracion',
        label: 'Configuración',
        icon: 'Settings',
        roles: MANAGEMENT,
        keywords: ['usuarios', 'sla', 'metas', 'plantillas', 'chatbot', 'importar', 'excel'],
      },
    ],
  },
];

/** Filtra la navegación por el rol de quien ha iniciado sesión. */
export function navigationFor(role: UserRole | undefined): NavSection[] {
  if (!role) return [];
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.roles || item.roles.includes(role)),
  })).filter((section) => section.items.length > 0);
}
